import moment from 'moment';

import { expandDivisionIdsForDisplay, normalizeDivisionIdsForSave } from '@src/packages/utils/divisions/divisionIdsUtils';

import type { ScheduleDashboardDateType } from '../screens/SchedulesDashboard/types';

const INTERVALS: ScheduleDashboardDateType[] = ['day', 'week', 'month', 'quarter', 'year'];

/** Parse the external QR contract in one place; malformed values never replace saved filters. */
export const parseReportUrlFilters = (search: string, entity: 'schedules' | 'templates' | 'routes' | 'operations') => {
  const params = new URLSearchParams(search);
  const match = /^abs:(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/.exec(params.get('range') ?? '');
  const relativeMatch = /^rel:(\d+):(day|week|month|quarter|year)$/.exec(params.get('range') ?? '');
  if (!match && !relativeMatch) return undefined;
  const from = moment(match?.[1] ?? moment().format('YYYY-MM-DD'), 'YYYY-MM-DD', true);
  const to = moment(match?.[2] ?? moment().format('YYYY-MM-DD'), 'YYYY-MM-DD', true);
  if (!from.isValid() || !to.isValid() || from.isAfter(to)) return undefined;
  const groupBy = INTERVALS.find(interval => interval === params.get('groupBy')) ?? 'day';
  const ids = (key: string) => Array.from(new Set((params.get(key) ?? '').split(',').filter(Boolean)));
  const selection = ids(entity);
  return {
    relative: { isRelative: Boolean(relativeMatch), relativeAmount: Math.max(1, Number(relativeMatch?.[1] ?? 1)), dateType: (INTERVALS.find(value => value === relativeMatch?.[2]) ?? 'day'), periodMode: 'absolute' as const },
    dateRange: [from, to] as [moment.Moment, moment.Moment],
    groupBy,
    templates: { selectedAll: selection.length === 0, byId: Object.fromEntries(selection.map(id => [id, { id, name: id }])) },
    schedules: { selectedAll: selection.length === 0, byId: Object.fromEntries(selection.map(id => [id, { id, name: id }])) },
    divisionIds: params.has('divisions') ? ids('divisions') : undefined,
  };
};

export const resolveReportUrlDivisions = (requested: string[], allowed: string[]) => {
  const accepted = requested.filter(id => allowed.includes(id));
  return {
    ids: requested.length ? (accepted.length ? accepted : allowed) : expandDivisionIdsForDisplay(requested, allowed),
    rejected: accepted.length !== requested.length,
  };
};

/** Only same-origin paths captured before login may be used as a return destination. */
export const consumeReportReturnPath = (fallback: string): string => {
  const path = sessionStorage.getItem('reportReturnPath');
  sessionStorage.removeItem('reportReturnPath');
  if (!path || !path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return fallback;
  const url = new URL(path, window.location.origin);
  return url.origin === window.location.origin ? `${url.pathname}${url.search}` : fallback;
};

export interface ReportUrlState {
  relative: { isRelative: boolean; relativeAmount: number; dateType: string };
  dateRange: [moment.Moment, moment.Moment];
  groupBy: string;
  schedules?: { selectedAll: boolean; byId: Record<string, unknown> };
  templates?: { selectedAll: boolean; byId: Record<string, unknown> };
}

export const serializeReportUrlFilters = (search: string, filters: ReportUrlState, entity: 'schedules' | 'templates' | 'routes' | 'operations', divisions: string[], allowedDivisionIds: string[] = []) => {
  const params = new URLSearchParams(search);
  const { relative } = filters;
  params.set('range', relative.isRelative
    ? `rel:${relative.relativeAmount}:${relative.dateType}`
    : `abs:${filters.dateRange[0].format('YYYY-MM-DD')}:${filters.dateRange[1].format('YYYY-MM-DD')}`);
  params.set('groupBy', filters.groupBy);
  params.set('divisions', Array.from(new Set(normalizeDivisionIdsForSave(Array.from(new Set(divisions)), allowedDivisionIds))).sort().join(','));
  if (entity !== 'operations') {
    const selection = entity === 'templates' ? filters.templates : filters.schedules;
    params.set(entity, selection?.selectedAll ? '' : Object.keys(selection?.byId ?? {}).sort().join(','));
  }
  return `?${params.toString()}`;
};
